import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { all_routes } from "../router/all_routes";
import CollapseHeader from "../../core/common/collapse-header/collapse-header";
import Table from "../../core/common/dataTable/index";
import BudgetsModal from "./modals/budgetsModal";
import { AppDispatch } from "../../core/data/redux/store";
import { useDispatch, useSelector } from "react-redux";
import { deleteBudget, fetchAllBudgets, } from "../../core/data/redux/payroll/Accounting/budgets/budgetsAction";
import { filterData } from "../../core/common/GlobalFunctions/tableFilters";
import StatusFilterHeader from "../../core/common/commonComponents/StatusFilterHeader";
import PageHeader from "../../core/common/commonComponents/PageHeader";
import { exportToCSV, exportToExcel, } from "../../core/common/GlobalFunctions/exportTableData";
import DeleteModal1 from "../../core/modals/deleteModal1";
import { message } from "antd";
import { formatDate } from "../../core/utils/configFormatters";
import { useAppConfig } from "../../customhooks/useAppConfig";
import { decryptData } from "../../core/utils/encryptUtils";

const Budgets = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [selectedStatus, setSelectedStatus] = useState("active");
  const [selectedSort, setSelectedSort] = useState<any>("");
  const [tableKey, setTableKey] = useState(0);
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState<boolean>(false);
  const [modalType, setModalType] = useState<"edit" | "create" | "">("");
  const [selectedRowData, setSelectedRowData] = useState<any>(null);
  const { budgets } = useSelector((state: any) => state.accounting.budget);

  const encryptedPermissions = localStorage.getItem('currentPermissions');
  const permissions: string[] = Array.isArray(decryptData(encryptedPermissions || '')) ? decryptData(encryptedPermissions || '') : [];

  const canAdd =
    permissions.includes("Add") || permissions.includes("AllowAll");
  const canView =
    permissions.includes("View") || permissions.includes("AllowAll");
  const canEdit =
    permissions.includes("Edit") || permissions.includes("AllowAll");
  const canDelete =
    permissions.includes("Delete") || permissions.includes("AllowAll");
  const canExport =
    permissions.includes("Export") || permissions.includes("AllowAll");

  const showModal = (type: "edit" | "create") => {
    setModalType(type);
    setIsModalVisible(true);
  };
  const handleCancel = () => {
    setIsModalVisible(false);
    setModalType("");
  };

  const handleDeleteBudget = async (id: number) => {
    try {
      const result = await dispatch(deleteBudget(id));
      if (result.meta.requestStatus === "fulfilled") {
        message.success("Expense Category deleted successfully");
        dispatch(fetchAllBudgets());
        setTableKey((prevKey) => prevKey + 1);
        setIsDeleteModalVisible(false);
      } else {
        message.error((result.payload as { error: string })?.error);
      }
    } catch (error) {
      message.error("Something went wrong");
    }
  };
  useEffect(() => {
    dispatch(fetchAllBudgets());
  }, [dispatch]);

  const filtereddata = useMemo(() => {
    const filters = selectedStatus && selectedStatus !== "All"
      ? { status: selectedStatus }
      : {};
    return filterData(budgets, filters, "createdAt", "createdAt", selectedSort);
  }, [budgets, selectedStatus, selectedSort]);

  const ExportData = filtereddata.map((item: any) => ({
    // budget_id: item.budget_id || "N/A",
    budgetTitle: item.budgetTitle || "N/A",
    budgetType: item.budgetType || "N/A",
    startDate: item.startDate ? new Date(item.startDate).toLocaleDateString("en-IN") : "N/A",
    endDate: item.endDate ? new Date(item.endDate).toLocaleDateString("en-IN") : "N/A",
    overallRevenue: item.overallRevenue ?? 0,
    overallExpense: item.overallExpense ?? 0,
    expectedProfit: item.expectedProfit ?? 0,
    tax: item.tax ?? 0,
    budgetAmount: item.budgetAmount ?? 0,
    status: item.status || "Inactive",
    // createdBy: item.createdBy || "N/A",
    // modifiedBy: item.modifiedBy || "N/A",
    createdAt: item.createdAt ? new Date(item.createdAt).toLocaleDateString("en-IN") : "N/A",
    modifiedAt: item.modifiedAt ? new Date(item.modifiedAt).toLocaleDateString("en-IN") : "N/A",
  }));


  useEffect(() => {
    setTableKey((prevKey) => prevKey + 1);
  }, [selectedStatus, selectedSort, budgets]);

  const config = useAppConfig();
  if (!config) return null;

  const columns = [
    {
      title: "Budget Title",
      dataIndex: "budgetTitle",
      sorter: (a: any, b: any) => a.budgetTitle.length - b.budgetTitle.length,
      render: (_: any, record: any) => (
        <div
          className="d-flex align-items-center"
          style={{ gap: 8 }}
          onMouseEnter={(e) => e.currentTarget.querySelector("button")?.style.setProperty("opacity", "1")}
          onMouseLeave={(e) => e.currentTarget.querySelector("button")?.style.setProperty("opacity", "0")}
        >
          <span>{record.budgetTitle}</span>
          {canEdit && (
            <button
              className="btn btn-link p-0"
              onClick={() => {
                showModal("edit");
                setSelectedRowData(record);
              }}
              style={{
                opacity: 0,
                cursor: "pointer",
                transition: "opacity 0.3s",
              }}
            >
              <i className="ti ti-pencil" />
            </button>
          )}
        </div>
      ),
    },
    {
      title: "Budget Type",
      dataIndex: "budgetType",
      sorter: (a: any, b: any) => a.budgetType.length - b.budgetType.length,
    },
    {
      title: "Start Date",
      dataIndex: "startDate",
      render: (text: string) => formatDate(text, config),
      sorter: (a: any, b: any) => a.startDate.length - b.startDate.length,
    },
    {
      title: "End Date",
      dataIndex: "endDate",
      render: (text: string) => formatDate(text, config),
      sorter: (a: any, b: any) => a.EndDate.length - b.EndDate.length,
    },
    {
      title: "Total Revenue",
      dataIndex: "overallRevenue",
      sorter: (a: any, b: any) =>
        a.overallRevenue.length - b.overallRevenue.length,
    },
    {
      title: "Total Expense",
      dataIndex: "overallExpense",
      sorter: (a: any, b: any) =>
        a.overallExpense.length - b.overallExpense.length,
    },
    {
      title: "Tax Amount",
      dataIndex: "tax",
      sorter: (a: any, b: any) => a.tax.length - b.tax.length,
    },
    {
      title: "Budget Amount",
      dataIndex: "budgetAmount",
      sorter: (a: any, b: any) => a.budgetAmount.length - b.budgetAmount.length,
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (text: string, record: any) => (
        <span
          className={`badge ${text === "active" ? "badge-success" : "badge-danger"
            } d-inline-flex align-items-center badge-xs`}
        >
          <i className="ti ti-point-filled me-1" />
          {text ? text.charAt(0).toUpperCase() + text.slice(1) : ""}

        </span>
      ),
    },
    {
      title: "Action",
      dataIndex: "actions",
      render: (text: string, record: any) => (
        <div>
          <div className="action-icon d-inline-flex">
            {/* Edit Button */}
            {canEdit && (
              <button
                className="me-2"
                onClick={() => {
                  showModal("edit");
                  setSelectedRowData(record);
                }}
              >
                <i className="ti ti-edit" />
              </button>
            )}

            {/* Delete Button */}
            {canDelete && (
              <button
                className={`me-2 ${record.status === "inactive" ? "opacity-50 cursor-not-allowed" : ""}`}
                disabled={record.status === "inactive"}
                onClick={() => {
                  if (record.status === "inactive") return;
                  setSelectedRowData(record);
                  setIsDeleteModalVisible(true);
                }}
              >
                <i className="ti ti-trash" />
              </button>
            )}
          </div>
        </div>
      ),
    },
  ];
  return (
    <>
      {/* Page Wrapper */}
      <div className="page-wrapper">
        <div className="content">
          {/* Breadcrumb */}
          <PageHeader
            title="Budgets"
            breadcrumbs={[
              {
                label: <i className="ti ti-smart-home" />,
                path: all_routes.adminDashboard,
              },
              { label: "Accounting" },
              { label: "Budgets List", isActive: true },
            ]}
            // listViewPath={all_routes.clientlist}
            listViewPath="#"
            onExportCSV={canExport ? () => exportToCSV(ExportData, "budgets_data") : undefined}
            onExportExcel={canExport ? () => exportToExcel(ExportData, "budgets_data") : undefined}
            onAdd={canAdd ? () => showModal("create") : undefined}
            addLabel="Add budget"
            exportEnabled={canExport}
            addButtonEnabled={canAdd}
            rightExtra={<CollapseHeader />}
          />
          {/* /Breadcrumb */}
          {/* Budgets list */}
          <div className="card">
            <StatusFilterHeader
              title="Budgets List"
              selectedStatus={selectedStatus}
              setSelectedStatus={setSelectedStatus}
              selectedSort={selectedSort}
              setSelectedSort={setSelectedSort}
            />
            <div className="card-body p-0">
              <Table
                key={tableKey}
                dataSource={filtereddata}
                columns={columns}
                Selection={false}
              />
            </div>
          </div>
          {/* /Budgets list */}
        </div>
        <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
          <p className="mb-0">2024 - 2025© hrmtree.</p>
          <p>
            Developed By{" "}
            <Link to="#" className="text-primary">
              Leadchain Systems
            </Link>
          </p>
        </div>
      </div>
      {/* /Page Wrapper */}

      <BudgetsModal
        modalType={modalType}
        handleCancel={handleCancel}
        isModalVisible={isModalVisible}
        selectedRowData={selectedRowData}
        setTableKey={setTableKey}
      />
      <DeleteModal1
        isVisible={isDeleteModalVisible}
        onClose={() => setIsDeleteModalVisible(false)}
        onConfirm={() =>
          selectedRowData?.budget_id &&
          handleDeleteBudget(selectedRowData.budget_id)
        }
      />
    </>
  );
};

export default Budgets;
